import { reactive, readonly } from 'vue';
import {
	getActiveAddress,
	getDefaultAddress,
	getPriceLowestTariff
} from './functions';
import { KEYS } from './types';
import { createBaseService } from './base-delivery';
import type { IState, DeliveryZonePayload, IDeliveryService } from './types';
import { user, brandy, menuPromos } from '@/api';
import { delivery } from '@/api';
import { getStorageParsedData } from '@/utils/functions';
import type { ICfUserAddress } from '@/types/interfaces/chocofood';
import type { ICoordinates } from '@/types/interfaces/common';
import type { Subsidy } from '@/types/interfaces/menu';
import type {
	TariffsPayload,
	TariffsReservePayload,
	TariffsPayloadCoordinates,
	TariffOrderItem
} from '@/types/interfaces/delivery/tariffs';

const ACTIVE_ADDRESS_KEY = 'default.activeAddress';
const TEMP_ADDRESS_KEY = 'default.tempAddress';

const storage = window.localStorage;
const defaultAddress = getDefaultAddress();
const activeAddress = getStorageParsedData(ACTIVE_ADDRESS_KEY) || defaultAddress; // prettier-ignore
const tempAddress = getStorageParsedData(TEMP_ADDRESS_KEY) || defaultAddress;

const state = reactive<IState>({
	activeAddress,
	tempAddress,
	userAddressList: []
});

const BaseService = createBaseService(state);

const setActiveAddress = (address: ICfUserAddress) => {
	state.activeAddress = address;
	storage.setItem(ACTIVE_ADDRESS_KEY, JSON.stringify(address));
};

const setTempAddress = (address: Partial<ICfUserAddress>) => {
	state.tempAddress = {
		...state.tempAddress,
		...address
	};

	storage.setItem(TEMP_ADDRESS_KEY, JSON.stringify(state.tempAddress));
};

const fetchUserAddresses = async () => {
	const list = await user.getUserAddresses();
	return (state.userAddressList = list);
};

const initUnauthUserAddresses = () => {
	const address = getStorageParsedData(ACTIVE_ADDRESS_KEY) || defaultAddress;

	if (address.id) {
		state.userAddressList = [address];
		setActiveAddress(address);
	}
};

const initUserAddresses = async (
	isAuthorized: boolean,
	coordinates: ICoordinates | null,
	onClearCb: () => void
) => {
	if (!isAuthorized) {
		return initUnauthUserAddresses();
	}

	await DeliveryService.fetchUserAddresses();
	if (!state.activeAddress.id) {
		setActiveAddress(getActiveAddress(state.userAddressList, coordinates));
	}
	let address = state.activeAddress;

	// Если адрес не был создан на сервере
	if (address.id === -1) {
		address = await createAuthAddress(address, onClearCb);
		return setActiveAddress(address);
	}

	/**
	 * Если не подтверждали адрес, берем ближайший
	 */
	if (needToConfirmAddress()) {
		setActiveAddress(getActiveAddress(state.userAddressList, coordinates));
	}
};

const clearTempAddress = () => {
	state.tempAddress = defaultAddress;
	storage.removeItem(TEMP_ADDRESS_KEY);
};

const clearActiveAddress = () => {
	state.activeAddress = defaultAddress;
	storage.removeItem(ACTIVE_ADDRESS_KEY);
};

const clearAddresses = () => {
	clearActiveAddress();
	clearTempAddress();
};

const createUnauthAddress = (address: ICfUserAddress) => {
	address = {
		...address,
		id: -1
	};

	state.userAddressList = [address];
	setActiveAddress(address);
	return address;
};

const createAuthAddress = async (
	address: ICfUserAddress,
	onClearCb: () => void
) => {
	const res = await user.createAddress(address);
	const sameAddressIndex = state.userAddressList.findIndex(a => {
		return a.id === res.id;
	});

	// если такой же адрес есть, обновляем его
	if (sameAddressIndex >= 0) {
		state.userAddressList[sameAddressIndex] = res;
	}
	// если нет, добавляем его в список
	else {
		state.userAddressList = [res, ...state.userAddressList];
		onClearCb();
	}

	return res;
};

/**
 * Создает адрес пользователя и обновляет список адресов
 */
const createAddress = async (
	address: ICfUserAddress,
	isAuthorized: boolean,
	onClearCb: () => void
) => {
	let newAddress = address;

	if (isAuthorized) {
		newAddress = await createAuthAddress(address, onClearCb);
	} else {
		newAddress = createUnauthAddress(address);
	}

	setActiveAddress(newAddress);
	return newAddress;
};

const editUserAddress = async (
	id: number,
	address: ICfUserAddress,
	isAuthorized: boolean
) => {
	/**
	 * Если пользователь не авторизован
	 */
	if (!isAuthorized) {
		state.userAddressList = [address];
		setActiveAddress(address);
		return;
	}

	await user.updateUserAddress(address);
	state.userAddressList = state.userAddressList.map(el => {
		return el.id === id ? address : el;
	});

	clearTempAddress();
};

const deleteAddress = async (
	id: number,
	isAuthorized: boolean,
	coordinates: ICoordinates | null
) => {
	if (!isAuthorized) {
		state.userAddressList = [];
		clearAddresses();
		return;
	}

	await user.deleteAddress(id);
	state.userAddressList = state.userAddressList.filter(item => item.id !== id);

	if (state.activeAddress.id === id) {
		const address = getActiveAddress(state.userAddressList, coordinates);
		setActiveAddress(address);
	}
};

const needToConfirmAddress = () => {
	return sessionStorage.getItem(KEYS.IS_ADDRESS_CONFIRMED_KEY) !== 'true';
};

const confirmAddress = () => {
	sessionStorage.setItem(KEYS.IS_ADDRESS_CONFIRMED_KEY, 'true');
};

// Метод получения тарифов
const fetchTariffs = async (
	origin: TariffsPayloadCoordinates,
	destination: TariffsPayloadCoordinates,
	locationId?: string
) => {
	const payload: TariffsPayload = {
		destination,
		origin
	};

	if (locationId) {
		payload.location_id = locationId;
	}

	let response = await delivery.getDeliveryTariffs(payload);

	if (response.length && response.some(tariff => !tariff.is_fastest)) {
		response = [getPriceLowestTariff(response)];
	}

	return response;
};

// Метод для получения и резервации тарифов
const fetchReserveTariffs = async (
	addressId: string,
	locationId: string,
	orderItems: TariffOrderItem[],
	preparationTime: number
) => {
	const payload: TariffsReservePayload = {
		destination: {
			address_id: addressId
		},
		location_id: locationId,
		order: {
			items: orderItems,
			preparation_time_minutes: preparationTime
		}
	};

	const tariff = await delivery.reserveDeliveryTariff(payload);

	return tariff;
};

const checkDeliveryZone = ({
	brandId,
	locationId,
	coordinates
}: DeliveryZonePayload) => {
	if (locationId) {
		return brandy
			.getInDeliveryZoneByLocation(locationId, coordinates)
			.then(res => res.data);
	}

	if (brandId) {
		return brandy
			.getInDeliveryZoneByBrand(brandId, coordinates)
			.then(res => res.data);
	}

	console.error('Не переданы параметры для проверки зоны доставки');

	return Promise.resolve({ is_in_delivery_zone: true, delivery_cost: 0 });
};

export const DeliveryService: IDeliveryService = {
	...BaseService,
	state: readonly(state) as IState,
	setTempAddress,
	setActiveAddress,
	fetchUserAddresses,
	initUserAddresses,
	clearTempAddress,
	clearActiveAddress,
	createAddress,
	editUserAddress,
	deleteAddress,
	needToConfirmAddress,
	confirmAddress,
	fetchTariffs,
	fetchReserveTariffs,
	checkDeliveryZone,
	fetchSubsidies: menuPromos.getSubsidiesByFilial
};
